import { useQuery } from '@tanstack/react-query';
import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatDate, todayIso } from '@/components/live';
import { referenceValues } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';
import { optionLabel } from '@/lib/practiceLists';

interface RequirementRow {
  id: string; party_role: string; kyc_type_id: string | null; kyc_type_label: string | null; kyc_category: string | null;
  geography_id: string | null; geography_name: string | null; transaction_type: string;
  is_mandatory: boolean; requires_expiry_date: boolean; requires_approval: boolean;
  effective_from: string; effective_to: string | null; status: string;
}

const PARTY_ROLES = [
  { id: 'farmer', label: 'Farmer' }, { id: 'nursery', label: 'Nursery' }, { id: 'supplier', label: 'Supplier' },
  { id: 'service_provider', label: 'Service provider' }, { id: 'contractor', label: 'Contractor' },
  { id: 'transporter', label: 'Transporter' }, { id: 'aggregator', label: 'Aggregator' }, { id: 'processor', label: 'Processor' },
  { id: 'exporter', label: 'Exporter' }, { id: 'buyer', label: 'Buyer' }, { id: 'auditor', label: 'Auditor' },
  { id: 'staff', label: 'Staff' }, { id: 'representative', label: 'Representative' },
];

const TRANSACTIONS = [
  { id: 'onboarding', label: 'Onboarding' }, { id: 'harvest', label: 'Harvest' }, { id: 'purchase', label: 'Purchase' },
  { id: 'sale', label: 'Sale' }, { id: 'movement', label: 'Movement / transit' }, { id: 'payment', label: 'Payment' },
  { id: 'audit', label: 'Audit' }, { id: 'document_share', label: 'Document sharing' }, { id: 'portal_access', label: 'Portal access' },
];

/** Groups a document type belongs to (`kyc_type` attributes->>'category'). Any document in the group satisfies it. */
const GROUPS = [
  { id: 'identity', label: 'Any identity document' },
  { id: 'land_right', label: 'Any land-right document' },
  { id: 'financial', label: 'Any bank / financial document' },
  { id: 'business', label: 'Any business registration' },
];

const STATUS = [{ id: 'active', label: 'Active' }, { id: 'draft', label: 'Draft' }, { id: 'inactive', label: 'Inactive' }];

const whatLabel = (row: RequirementRow) => (row.kyc_category ? optionLabel(GROUPS, row.kyc_category) : row.kyc_type_label ?? '--');

/**
 * Which KYC documents each kind of party must hold, and for what (PENDING item 7: the framework was
 * built, but nothing could write it). A requirement names ONE document type or ONE group - "any
 * identity document" - because naming a single document decides who can be enrolled: a smallholder
 * may hold a Voter ID or an MGNREGA card and not Aadhaar, and a forest-dwelling farmer holds an FRA
 * patta, not a Record of Rights. A place narrows it; left empty it applies everywhere.
 */
export default function KycRequirementsScreen() {
  const types = useQuery({ queryKey: ['ref', 'kyc_type'], queryFn: () => referenceValues('kyc_type'), enabled: isSupabaseConfigured });
  const typeOptions = (types.data ?? []).map((row) => ({ id: row.id, label: row.label }));
  return (
    <RegisterScreen<RequirementRow>
      addLabel="Add requirement" exportName="kyc-requirements" recordLabel={(row) => `requirement ${optionLabel(PARTY_ROLES, row.party_role)} · ${whatLabel(row)}`}
      recordNoun="requirement"
      screenKey="party.kyc.requirements" searchColumns={['party_role', 'kyc_type_label', 'kyc_category', 'geography_name', 'transaction_type']}
      subtitle="Which documents each kind of party must hold, for which transaction - one document, or any document in a group."
      table="party.party_kyc_requirements" title="KYC requirements" view="party_kyc_requirements" orderBy={{ column: 'party_role', ascending: true }}
      columns={[
        { key: 'party_role', label: 'Party', render: (row) => <strong>{optionLabel(PARTY_ROLES, row.party_role)}</strong> },
        { key: 'transaction_type', label: 'For', render: (row) => optionLabel(TRANSACTIONS, row.transaction_type) },
        { key: 'kyc_type_label', label: 'Document', render: (row) => <span className="cell-stack"><span>{whatLabel(row)}</span>{row.kyc_category && <small className="muted">any document in the group</small>}</span> },
        { key: 'geography_name', label: 'Where', render: (row) => row.geography_name ?? 'Everywhere' },
        { key: 'is_mandatory', label: 'Mandatory', render: (row) => (row.is_mandatory ? 'Yes' : 'Optional') },
        { key: 'requires_expiry_date', label: 'Expiry', render: (row) => (row.requires_expiry_date ? 'Asked' : '--') },
        { key: 'effective_from', label: 'From', render: (row) => `${formatDate(row.effective_from)}${row.effective_to ? ` – ${formatDate(row.effective_to)}` : ''}` },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      stats={[
        { key: 'active', label: 'Active', tone: 'ok', match: (row) => row.status === 'active' },
        { key: 'mandatory', label: 'Mandatory', tone: 'warn', match: (row) => row.is_mandatory },
        { key: 'groups', label: 'Any-of-group', tone: 'info', match: (row) => Boolean(row.kyc_category) },
        { key: 'draft', label: 'Draft', tone: 'neutral', match: (row) => row.status === 'draft' },
      ]}
      defaults={{ party_role: 'farmer', transaction_type: 'onboarding', kyc_category: 'identity', effective_from: todayIso(),
                  is_mandatory: true, requires_approval: true, requires_expiry_date: false, status: 'active' }}
      fields={[
        { key: 'party_role', label: 'Kind of party', type: 'select', options: PARTY_ROLES, required: true },
        { key: 'transaction_type', label: 'Needed for', type: 'select', options: TRANSACTIONS, required: true },
        { key: 'kyc_category', label: 'Group of documents', type: 'select', options: GROUPS, placeholder: 'A single document instead',
          hint: 'Any document in the group will do. Leave empty to name one document below.' },
        { key: 'kyc_type_id', label: 'One document', type: 'select', options: typeOptions, required: true,
          showIf: (form) => !form.kyc_category, hint: 'Only this document will do.' },
        { key: 'geography_id', label: 'Only in', type: 'village', geographyType: 'state',
          hint: 'Leave empty to apply everywhere. A state applies to every place in it.' },
        { key: 'is_mandatory', label: 'Mandatory', type: 'check', placeholder: 'Blocks the transaction until held' },
        { key: 'requires_expiry_date', label: 'Expiry', type: 'check', placeholder: 'Ask for a valid-to date' },
        { key: 'requires_approval', label: 'Approval', type: 'check', placeholder: 'A second person verifies it' },
        { key: 'effective_from', label: 'Effective from', type: 'date', required: true },
        { key: 'effective_to', label: 'Effective to', type: 'date' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS, required: true },
      ]}
      validate={(form) => ({
        effective_to: form.effective_to && form.effective_from && String(form.effective_to) < String(form.effective_from)
          ? 'Cannot be before Effective from.' : undefined,
      })}
    />
  );
}
